import { Editor } from '@tiptap/react';
import axiosInstance from '../../../api/axiosConfig';
import { convertImageToPNG } from '../../../utils/imageUtils';

// A plain module: no hook here. The caller passes the messages down from the
// component that holds the language pack.
export interface DocumentHelperMessages {
    imagePasteFailed: string;
    imageUploadFailed: string;
    exportFailed: string;
}

export const normalizeHTMLContent = (content: string): string => {
    if (!content) return '<p></p>';
    
    
    // Check if content has any HTML tags
    const hasHTMLTags = /<[^>]+>/g.test(content);
    
    if (!hasHTMLTags) {
        // Plain text - wrap each line in a paragraph
        const lines = content.split('\n').filter(line => line.trim().length > 0);
        
        if (lines.length === 0) {
            return '<p></p>';
        }
        
        const wrappedContent = lines.map(line => {
            const trimmed = line.trim();
            if (trimmed) {
                return `<p>${trimmed}</p>`;
            }
            return '';
        }).filter(line => line).join('\n');
        
        return wrappedContent;
    }
    
    // Has HTML tags but might be malformed
    let normalized = content;
    
    // Remove empty list items that cause duplication issues
    normalized = normalized.replace(/<li>\s*<\/li>/g, '');
    normalized = normalized.replace(/<li><p><\/p><\/li>/g, '');
    normalized = normalized.replace(/<li><p>\s*<\/p><\/li>/g, '');
    
    // Remove empty paragraphs at the start of list items
    normalized = normalized.replace(/<li><p><\/p>/g, '<li>');

    // A block whose only content is a <br> is a blank line, not a paragraph
    // with a line break in it. The mobile editor writes them that way —
    // execCommand splits a paragraph into <p><br></p> — and TipTap would
    // otherwise read the <br> as a hardBreak node, making the block two lines
    // tall and hiding it from the CSS that collapses blank paragraphs, so
    // every blank line the writer added on the phone came back several lines
    // deep. Attributes are preserved: they carry the block's spacing.
    normalized = normalized.replace(/<p([^>]*)>(?:\s|&nbsp;|<br\s*\/?>)*<\/p>/gi, '<p$1></p>');
    // Enter at the top level of the mobile editor produces a bare <div>
    // instead; same blank line, same treatment. Only the attribute-less form —
    // a <div> carrying attributes is something else.
    normalized = normalized.replace(/<div>(?:\s|&nbsp;|<br\s*\/?>)*<\/div>/gi, '<p></p>');

    return normalized;
};

export const calculateWordCount = (editor: Editor | null) => {
    if (!editor) return { pages: 0, words: 0, characters: 0, charactersNoSpaces: 0 };
    
    // Get plain text from editor
    const text = editor.getText();
    
    // Calculate statistics
    const words = text.trim().split(/\s+/).filter(word => word.length > 0).length;
    const characters = text.length;
    const charactersNoSpaces = text.replace(/\s/g, '').length;
    
    // Estimate pages (assuming ~250 words per page, standard manuscript format)
    const pages = Math.ceil(words / 250);
    
    return {
        pages,
        words,
        characters,
        charactersNoSpaces
    };
};

export const uploadImageToServer = async (file: File, documentId: number): Promise<string> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_id', documentId.toString());
    
    try {
        const response = await axiosInstance.post('/upload-document-image/', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        
        return response.data.url;
    } catch (error) {
        console.error('Image upload error:', error);
        throw error;
    }
};

export const handleImagePaste = async (
    event: ClipboardEvent,
    editor: Editor | null,
    documentId: number,
    messages: DocumentHelperMessages
) => {
    if (!editor) return;
    
    const items = event.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
        if (items[i].type.indexOf('image') !== -1) {
            event.preventDefault();
            const blob = items[i].getAsFile();
            if (blob) {
                try {
                    // Convert to PNG before uploading
                    const pngFile = await convertImageToPNG(blob);
                    
                    // Upload to server
                    const imageUrl = await uploadImageToServer(pngFile, documentId);
                    editor.chain().focus().setImage({ src: imageUrl }).run();
                } catch (error) {
                    console.error('Failed to upload pasted image:', error);
                    alert(messages.imagePasteFailed);
                }
            }
            break;
        }
    }
};

export const insertImageFromUrl = async (
    editor: Editor | null,
    documentId: number,
    messages: DocumentHelperMessages
) => {
    if (!editor) return;
    
    // Create a hidden file input
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    
    input.onchange = async (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (!file) return;
        
        // Show loading state (you could add a loading indicator)
        try {
            // Convert to PNG before uploading
            const pngFile = await convertImageToPNG(file);
            const imageUrl = await uploadImageToServer(pngFile, documentId);
            editor.chain().focus().setImage({ src: imageUrl }).run();
        } catch (error) {
            console.error('Failed to upload image:', error);
            alert(messages.imageUploadFailed);
        }
    };
    
    // Trigger the file picker
    input.click();
};

export type ExportFormat = 'pdf' | 'docx' | 'rtf' | 'html';

export const exportDocument = async (
    documentId: number,
    format: ExportFormat,
    name: string | undefined,
    messages: DocumentHelperMessages
) => {
    try {
        const response = await axiosInstance.get(
            `/documents/${documentId}/export/?fmt=${format}`,
            { responseType: 'blob' }
        );
        const url = URL.createObjectURL(response.data);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = `${name || 'document'}.${format}`;
        anchor.click();
        URL.revokeObjectURL(url);
    } catch (error) {
        console.error('Document export failed:', error);
        alert(messages.exportFailed);
    }
};
